import React, { useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { Invoice } from "../components/Invoice";
import { toast } from "sonner";
import {
  DoorOpen,
  KeyRound,
  ShieldCheck,
  CheckCircle2,
  Circle,
  Timer,
  Laptop,
  MapPin,
  Plus,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

// 🔥 STEP 1: StatusChip ko clickable banaya aur onClick event attach kiya
const StatusChip = ({ status, onClick }) => {
  const isClickable = status === "Assigned" || status === "In-Progress";
  const clickProps = isClickable && onClick ? { onClick, style: { cursor: "pointer" }, title: "Click to open OTP tracking" } : {};

  if (status === "Assigned")
    return (
      <span className="chip chip-warn hover:opacity-80 transition-opacity" data-testid="status-badge-assigned" {...clickProps}>
        <span className="dot dot-warn" /> Technician assigned
      </span>
    );
  if (status === "In-Progress")
    return (
      <span className="chip chip-brand hover:opacity-80 transition-opacity" data-testid="status-badge-inprogress" {...clickProps}>
        <span className="dot dot-brand pulse" /> Repair in progress
      </span>
    );
  if (status === "Completed")
    return (
      <span className="chip chip-success" data-testid="status-badge-completed">
        <span className="dot dot-success" /> Completed
      </span>
    );
  return <span className="chip chip-slate" {...clickProps}>{status}</span>;
};

const Stepper = ({ status }) => {
  const steps = [
    { k: "Assigned", label: "Booked" },
    { k: "In-Progress", label: "In progress" },
    { k: "Completed", label: "Completed" },
  ];
  const idx = steps.findIndex((s) => s.k === status);
  return (
    <div className="flex items-center gap-3">
      {steps.map((s, i) => {
        const done = i < idx;
        const active = i === idx;
        return (
          <React.Fragment key={s.k}>
            <div className="flex items-center gap-2">
              <div
                className={
                  "stepper-node " + (done ? "stepper-done" : active ? "stepper-active" : "")
                }
              >
                {done ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <div className="hidden sm:block text-xs text-[var(--ink-2)] font-medium">
                {s.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div
                className={"h-px w-8 sm:w-10 " + (i < idx ? "bg-[var(--success)]" : "bg-[var(--line-strong)]")}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const GateOTPPanel = ({ booking }) => {
  const { verifyGateOTP } = useApp();
  const [code, setCode] = useState("");
  const locked = booking.status !== "Assigned";

  const submit = async(e) => {
    e.preventDefault();
    if (code.length !== 4) return toast.error("Enter the 4-digit code");
    const ok = await verifyGateOTP(booking.id, code);
    if (ok) {
      toast.success("Code verified · repair unlocked");
      setCode("");
    } else {
      toast.error("Code mismatch — please check with the technician");
    }
  };

  return (
    <div className="card p-6" data-testid={`gate-otp-panel-${booking.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <DoorOpen size={16} className="text-[var(--brand)]" />
          <div className="text-sm font-semibold text-[var(--ink-1)]">Gate arrival code</div>
        </div>
        {locked ? (
          <span className="chip chip-success">
            <CheckCircle2 size={12} /> Verified
          </span>
        ) : (
          <span className="chip chip-warn">
            <Timer size={12} /> Pending
          </span>
        )}
      </div>
      <p className="text-sm text-[var(--ink-2)] leading-relaxed">
        When the technician reaches your gate, they'll show you a 4-digit code on their handheld
        device. Enter it below to authorize entry and start the repair.
      </p>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
        <div className="card-soft p-4 rounded-2xl">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">
            ENTER THE CODE 
          </div>
          <div className="text-2xl font-semibold tracking-[0.4em] text-[var(--brand-deep)] mt-1">
            {}
          </div>
          <div className="text-xs text-[var(--ink-3)] mt-1">ASK TO TECHNICIAN</div>
        </div>
        <form onSubmit={submit} className="space-y-2">
          <input
            className="field field-mono"
            placeholder="• • • •"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
            disabled={locked}
            data-testid={`gate-otp-input-${booking.id}`}
            maxLength={4}
            inputMode="numeric"
          />
          <button
            className="btn-primary w-full justify-center"
            disabled={locked}
            data-testid={`gate-otp-verify-${booking.id}`}
          >
            {locked ? "Verified" : "Verify & unlock"}
          </button>
        </form>
      </div>
    </div>
  );
};

const HandoverTokenPanel = ({ booking }) => {
  const completed = booking.status === "Completed";
  return (
    <div className="card p-6" data-testid={`handover-token-panel-${booking.id}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-[var(--success)]" />
          <div className="text-sm font-semibold text-[var(--ink-1)]">Handover code</div>
        </div>
        {completed ? (
          <span className="chip chip-success">
            <CheckCircle2 size={12} /> Settled
          </span>
        ) : (
          <span className="chip chip-warn">
            <KeyRound size={12} /> Do not share yet
          </span>
        )}
      </div>

      <div className="card-success p-6 text-center rounded-2xl">
        <div className="text-xs uppercase tracking-wider text-[#166534] font-semibold">
          Your secure handover code
        </div>
        <div
          className="text-4xl md:text-5xl font-display tracking-[0.3em] text-[#166534] mt-2"
          data-testid={`handover-token-value-${booking.id}`}
        >
          {booking.handoverToken}
        </div>
        <p className="text-xs text-[#166534]/80 mt-3 max-w-xs mx-auto leading-relaxed">
          Share this code with your technician <strong>only after</strong> verifying your laptop is
          100% repaired and working perfectly.
        </p>
      </div>
    </div>
  );
};

const ActiveBookingCard = ({ booking }) => {
  return (
    <div className="grid lg:grid-cols-12 gap-5">
      <div className="lg:col-span-12 card p-6" data-testid={`active-booking-${booking.id}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <StatusChip status={booking.status} />
              <span className="text-xs text-[var(--ink-3)] font-medium">#{booking.id}</span>
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-[var(--ink-1)] mt-2">
              {booking.brand} <span className="text-[var(--ink-3)]">·</span>{" "}
              <span className="text-[var(--ink-2)]">{booking.model}</span>
            </h3>
            <div className="text-sm text-[var(--ink-2)] mt-1 flex items-center gap-2">
              <MapPin size={13} /> {booking.address}
            </div>
          </div>
          <Stepper status={booking.status} />
        </div>

        <div className="mt-5 card-soft p-4 rounded-2xl">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">
            Reported issue
          </div>
          <div className="text-sm text-[var(--ink-1)] mt-1 font-medium">
            {booking.issue === "None of the Above / Other Issue" && booking.customIssue
              ? booking.customIssue
              : booking.issue}
          </div>
          {booking.description && (
            <div className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
              {booking.description}
            </div>
          )}
        </div>
      </div>

      <div className="lg:col-span-6">
        <GateOTPPanel booking={booking} />
      </div>
      <div className="lg:col-span-6">
        <HandoverTokenPanel booking={booking} />
      </div>
    </div>
  );
};

// 🔥 STEP 2: HistoryRow mein click handler pass kiya status badge ke liye
const HistoryRow = ({ booking, onStatusClick }) => {
  const [open, setOpen] = useState(false);
  const completed = booking.status === "Completed";
  return (
    <div className="card p-5" data-testid={`history-row-${booking.id}`}>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="w-10 h-10 rounded-xl bg-[var(--bg-soft)] border border-[var(--line)] flex items-center justify-center">
            <Laptop size={16} className="text-[var(--brand)]" />
          </div>
          <div>
            <div className="text-[var(--ink-1)] text-sm font-semibold">
              {booking.brand} · {booking.model}
            </div>
            <div className="text-xs text-[var(--ink-3)] mt-0.5">
              #{booking.id} · {new Date(booking.createdAt).toLocaleString("en-IN")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Status chip par click karne par active card badal jaega */}
          <StatusChip status={booking.status} onClick={onStatusClick} />
          {completed ? (
            <button
              onClick={() => setOpen((v) => !v)}
              className="btn-secondary"
              data-testid={`view-invoice-btn-${booking.id}`}
            >
              {open ? (
                <>
                  Hide invoice <ChevronUp size={14} />
                </>
              ) : (
                <>
                  View invoice <ChevronDown size={14} />
                </>
              )}
            </button>
          ) : (
            <span className="chip chip-slate">
              <Timer size={12} /> Invoice pending
            </span>
          )}
        </div>
      </div>
      {completed && open && (
        <div className="mt-5">
          <Invoice booking={booking} />
        </div>
      )}
    </div>
  );
};

export const CustomerHub = () => {
  const { session, bookingsForCurrentUser, setRoute } = useApp();
  
  // 🔥 STEP 3: State banayi taaki user jis card ko click kare, wahi active dikhe
  const [selectedActiveId, setSelectedActiveId] = useState(null);

  // Default active nikalne ka logic jo pehle tha
  const defaultActive = useMemo(
    () => bookingsForCurrentUser.find((b) => b.status !== "Completed"),
    [bookingsForCurrentUser]
  );

  // Agar user ne kisi specific booking ka status click kiya hai, toh use active banao, nahi toh default chalne do
  const active = useMemo(() => {
    if (selectedActiveId) {
      return bookingsForCurrentUser.find((b) => b.id === selectedActiveId) || defaultActive;
    }
    return defaultActive;
  }, [bookingsForCurrentUser, selectedActiveId, defaultActive]);

  const history = useMemo(
    () => bookingsForCurrentUser.filter((b) => !active || b.id !== active.id),
    [bookingsForCurrentUser, active]
  );

  if (!session || session.role !== "customer") return null;

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="card p-6 mb-8" data-testid="session-profile">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--brand-deep)] font-semibold">
              My Account
            </div>
            <div className="font-display text-3xl text-[var(--ink-1)] mt-1">
              Hi, <span className="text-[var(--brand)]">+91 {session.phone}</span>
            </div>
            <div className="text-sm text-[var(--ink-2)] mt-1">
              Track your repair, view invoices, and book a new visit anytime.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setRoute("landing")}
              className="btn-primary"
              data-testid="new-booking-btn"
            >
              <Plus size={14} /> New booking
            </button>
          </div>
        </div>
      </div>

      {active ? (
        <ActiveBookingCard booking={active} />
      ) : (
        <div className="card p-10 text-center" data-testid="no-active-booking">
          <Circle size={22} className="text-[var(--ink-4)] mx-auto" />
          <div className="font-display text-xl text-[var(--ink-1)] mt-3">No active booking</div>
          <p className="text-[var(--ink-2)] text-sm mt-1">
            Book a repair to see live tracking here.
          </p>
          <button
            onClick={() => setRoute("landing")}
            className="btn-primary mt-5"
            data-testid="book-first-btn"
          >
            Book a repair
          </button>
        </div>
      )}

      <div className="mt-12">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-semibold">
              Booking history
            </div>
            <h3 className="font-display text-2xl text-[var(--ink-1)] mt-1">Past bookings</h3>
          </div>
          <div className="text-xs text-[var(--ink-3)]">
            {history.length} record{history.length === 1 ? "" : "s"}
          </div>
        </div>
        {history.length === 0 ? (
          <div className="card p-6 text-sm text-[var(--ink-2)]" data-testid="history-empty">
            No prior bookings yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="history-list">
            {history.map((b) => (
              <HistoryRow 
                key={b.id} 
                booking={b} 
                // Click hone par state update hogi aur card top section mein move ho jayega
                onStatusClick={() => {
                  setSelectedActiveId(b.id);
                  toast.success(`Tracking loaded for order #${b.id}`);
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};